import type { Deck } from '../types/deck';

/**
 * Deck handles are stored without `@`; gallery search often types `@slug`.
 * Assumes `qLower` is already `trim().toLowerCase()`.
 */
export function deckSearchHandleQuerySlice(qLower: string): string {
  return qLower.startsWith('@') ? qLower.slice(1) : qLower;
}

/** Signed-in user hint for deck gallery author autocomplete (avatar + slug). */
export interface DeckGalleryViewerAuthor {
  slug: string;
  displayName: string;
  photoURL: string | null;
}

export interface DeckGalleryAuthorSuggestion {
  slug: string;
  displayName: string;
  photoURL: string | null;
  /** Row is the current viewer — pinned when search is empty. */
  isViewer: boolean;
}

/** Unique authors from deck handles; viewer row first with live avatar when provided. */
export function buildDeckGalleryAuthorSuggestions(
  decks: Deck[],
  viewer: DeckGalleryViewerAuthor | null | undefined,
): DeckGalleryAuthorSuggestion[] {
  const bySlug = new Map<string, string>();
  for (const d of decks) {
    const slug = d.ownerHandle?.trim();
    if (!slug || bySlug.has(slug)) continue;
    bySlug.set(slug, (d.ownerDisplayName?.trim() || slug));
  }

  const vSlug = viewer?.slug?.trim();

  const others: DeckGalleryAuthorSuggestion[] = [...bySlug.entries()]
    .filter(([slug]) => slug !== vSlug)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([slug, displayName]) => ({
      slug,
      displayName,
      photoURL: null as string | null,
      isViewer: false,
    }));

  if (!vSlug || !viewer) return others;

  return [
    {
      slug: vSlug,
      displayName: viewer.displayName?.trim() || vSlug,
      photoURL: viewer.photoURL ?? null,
      isViewer: true,
    },
    ...others,
  ];
}

/** Match slug / display name against normalized gallery query (ignore leading `@` on slug side via qh). */
export function deckGalleryAuthorMatchesQuery(
  slug: string,
  displayName: string,
  qRawTrimmed: string,
): boolean {
  const ql = qRawTrimmed.trim().toLowerCase();
  if (!ql) return true;
  const qh = deckSearchHandleQuerySlice(ql);
  const sl = slug.toLowerCase();
  const dn = displayName.trim().toLowerCase();
  return sl.includes(qh) || dn.includes(ql) || dn.includes(qh);
}

/** Includes owner handle + display name, subtitle/description, tags. */
export function deckMatchesGalleryTextSearch(deck: Deck, qLower: string): boolean {
  const qh = deckSearchHandleQuerySlice(qLower);
  const handle = deck.ownerHandle.toLowerCase();
  const display = deck.ownerDisplayName.toLowerCase();
  return (
    deck.title.toLowerCase().includes(qLower) ||
    handle.includes(qh) ||
    display.includes(qLower) ||
    display.includes(qh) ||
    deck.description.toLowerCase().includes(qLower) ||
    deck.tags.some((t) => t.toLowerCase().includes(qLower)) ||
    Boolean(deck.metafy?.title?.toLowerCase().includes(qLower))
  );
}
